import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  getIndustrialRiskQualityIndex,
  getIndustrialRiskQualityMix,
  getIndustrialRiskQualityMixBySupplier,
  getIndustrialRiskQualityMixSupplier,
} from '../services/industrialRiskService';

import { esNumberFormat } from '../../industrialCost/utils/numberFormat';
type Tone = 'success' | 'warning' | 'error' | 'default';

type QualityMixRow = {
  quality_code?: string;
  label?: string;
  rank?: number;
  units?: number;
  units_share?: number;
  revenue_eur?: number;
  revenue_share?: number;
};

type SkuUnitsRow = {
  sku_code?: string;
  units?: number;
};

type SkuRevenueRow = {
  sku_code?: string;
  revenue_eur?: number;
};

type RankingRow = {
  supplier?: string;
  total_units?: number;
  total_revenue_eur?: number;
  unmapped_units?: number;
  unmapped_units_share?: number;
  premium_share?: number;
  low_share?: number;
  by_quality?: QualityMixRow[];
};

type QualityReport = {
  decision_grade?: boolean;
  reasons: string[];
  note?: string;
  data_coverage?: {
    supplier_lots_considered?: number;
    n_outputs_rows?: number;
    n_purchases_rows?: number;
    n_mapped_skus?: number;
    n_qualities_defined?: number;
    n_suppliers?: number;
  };
};

type GlobalMixResponse = {
  status?: string;
  product_code?: string;
  supplier?: string | null;
  mix?: {
    total_units?: number;
    total_revenue_eur?: number;
    unmapped_units?: number;
    unmapped_units_share?: number;
    by_quality?: QualityMixRow[];
    top_skus_by_units?: SkuUnitsRow[];
    top_unmapped_skus_by_units?: SkuUnitsRow[];
  };
  quality_report?: QualityReport;
};

type RankingResponse = {
  status?: string;
  product_code?: string;
  top_n?: number;
  sort_by?: string;
  ranking?: RankingRow[];
  quality_report?: QualityReport;
};

type SupplierDetailResponse = {
  status?: string;
  product_code?: string;
  supplier?: string;
  kpis?: {
    total_units?: number;
    total_revenue_eur?: number;
    unmapped_units?: number;
    unmapped_units_share?: number;
  };
  by_quality?: QualityMixRow[];
  top_skus_by_units?: SkuUnitsRow[];
  top_skus_by_revenue?: SkuRevenueRow[];
  quality_report?: QualityReport;
};

type QualityIndexResponse = {
  status?: string;
  product_code?: string;
  supplier?: string;
  distribution?: {
    p10?: number;
    p50?: number;
    p90?: number;
  };
  lots_preview?: Array<{
    purchase_lot_key?: string;
    quality_index?: number;
    units?: number;
    unmapped_units?: number;
  }>;
  quality_report?: QualityReport;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function asArray<T = unknown>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function eur(value?: number | null, digits = 0): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return esNumberFormat({
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

function fmtNumber(value?: number | null, digits = 0): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return esNumberFormat({
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

function pct01(value?: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

function indexFmt(value?: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return value.toFixed(3);
}

function qualityBandLabel(value?: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  if (value >= 0.7) return 'Muy alta';
  if (value >= 0.55) return 'Alta';
  if (value >= 0.4) return 'Media';
  if (value >= 0.25) return 'Baja';
  return 'Muy baja';
}

function normalizeQualityReport(raw: unknown): QualityReport | undefined {
  if (!isRecord(raw)) return undefined;

  const coverageRaw = isRecord(raw.data_coverage) ? raw.data_coverage : undefined;

  return {
    decision_grade: typeof raw.decision_grade === 'boolean' ? raw.decision_grade : undefined,
    reasons: asArray(raw.reasons).filter((item): item is string => typeof item === 'string'),
    note: asString(raw.note),
    data_coverage: coverageRaw
      ? {
          supplier_lots_considered: asNumber(coverageRaw.supplier_lots_considered),
          n_outputs_rows: asNumber(coverageRaw.n_outputs_rows),
          n_purchases_rows: asNumber(coverageRaw.n_purchases_rows),
          n_mapped_skus: asNumber(coverageRaw.n_mapped_skus),
          n_qualities_defined: asNumber(coverageRaw.n_qualities_defined),
          n_suppliers: asNumber(coverageRaw.n_suppliers),
        }
      : undefined,
  };
}

function normalizeQualityMixRow(raw: unknown): QualityMixRow | null {
  if (!isRecord(raw)) return null;
  return {
    quality_code: asString(raw.quality_code),
    label: asString(raw.label),
    rank: asNumber(raw.rank),
    units: asNumber(raw.units),
    units_share: asNumber(raw.units_share),
    revenue_eur: asNumber(raw.revenue_eur),
    revenue_share: asNumber(raw.revenue_share),
  };
}

function normalizeSkuUnitsRow(raw: unknown): SkuUnitsRow | null {
  if (!isRecord(raw)) return null;
  return {
    sku_code: asString(raw.sku_code),
    units: asNumber(raw.units),
  };
}

function normalizeSkuRevenueRow(raw: unknown): SkuRevenueRow | null {
  if (!isRecord(raw)) return null;
  return {
    sku_code: asString(raw.sku_code),
    revenue_eur: asNumber(raw.revenue_eur),
  };
}

function normalizeGlobalMix(raw: unknown): GlobalMixResponse {
  const root = isRecord(raw) ? raw : {};
  const mixRaw = isRecord(root.mix) ? root.mix : undefined;

  return {
    status: asString(root.status),
    product_code: asString(root.product_code),
    supplier: asString(root.supplier),
    mix: mixRaw
      ? {
          total_units: asNumber(mixRaw.total_units),
          total_revenue_eur: asNumber(mixRaw.total_revenue_eur),
          unmapped_units: asNumber(mixRaw.unmapped_units),
          unmapped_units_share: asNumber(mixRaw.unmapped_units_share),
          by_quality: asArray(mixRaw.by_quality)
            .map(normalizeQualityMixRow)
            .filter((item): item is QualityMixRow => Boolean(item)),
          top_skus_by_units: asArray(mixRaw.top_skus_by_units)
            .map(normalizeSkuUnitsRow)
            .filter((item): item is SkuUnitsRow => Boolean(item)),
          top_unmapped_skus_by_units: asArray(mixRaw.top_unmapped_skus_by_units)
            .map(normalizeSkuUnitsRow)
            .filter((item): item is SkuUnitsRow => Boolean(item)),
        }
      : undefined,
    quality_report: normalizeQualityReport(root.quality_report),
  };
}

function normalizeRanking(raw: unknown): RankingResponse {
  const root = isRecord(raw) ? raw : {};

  return {
    status: asString(root.status),
    product_code: asString(root.product_code),
    top_n: asNumber(root.top_n),
    sort_by: asString(root.sort_by),
    ranking: asArray(root.ranking).reduce<RankingRow[]>((acc, item) => {
      if (!isRecord(item)) return acc;

      const supplier = asString(item.supplier);
      if (!supplier) return acc;

      acc.push({
        supplier,
        total_units: asNumber(item.total_units),
        total_revenue_eur: asNumber(item.total_revenue_eur),
        unmapped_units: asNumber(item.unmapped_units),
        unmapped_units_share: asNumber(item.unmapped_units_share),
        premium_share: asNumber(item.premium_share),
        low_share: asNumber(item.low_share),
        by_quality: asArray(item.by_quality)
          .map(normalizeQualityMixRow)
          .filter((x): x is QualityMixRow => x !== null),
      });

      return acc;
    }, []),
    quality_report: normalizeQualityReport(root.quality_report),
  };
}

function normalizeSupplierDetail(raw: unknown): SupplierDetailResponse {
  const root = isRecord(raw) ? raw : {};
  const kpisRaw = isRecord(root.kpis) ? root.kpis : undefined;

  return {
    status: asString(root.status),
    product_code: asString(root.product_code),
    supplier: asString(root.supplier),
    kpis: kpisRaw
      ? {
          total_units: asNumber(kpisRaw.total_units),
          total_revenue_eur: asNumber(kpisRaw.total_revenue_eur),
          unmapped_units: asNumber(kpisRaw.unmapped_units),
          unmapped_units_share: asNumber(kpisRaw.unmapped_units_share),
        }
      : undefined,
    by_quality: asArray(root.by_quality)
      .map(normalizeQualityMixRow)
      .filter((item): item is QualityMixRow => Boolean(item)),
    top_skus_by_units: asArray(root.top_skus_by_units)
      .map(normalizeSkuUnitsRow)
      .filter((item): item is SkuUnitsRow => Boolean(item)),
    top_skus_by_revenue: asArray(root.top_skus_by_revenue)
      .map(normalizeSkuRevenueRow)
      .filter((item): item is SkuRevenueRow => Boolean(item)),
    quality_report: normalizeQualityReport(root.quality_report),
  };
}

function normalizeQualityIndex(raw: unknown): QualityIndexResponse {
  const root = isRecord(raw) ? raw : {};
  const distributionRaw = isRecord(root.distribution) ? root.distribution : undefined

  return {
    status: asString(root.status),
    product_code: asString(root.product_code),
    supplier: asString(root.supplier),
    distribution: distributionRaw
      ? {
          p10: asNumber(distributionRaw.p10),
          p50: asNumber(distributionRaw.p50),
          p90: asNumber(distributionRaw.p90),
        }
      : undefined,
    lots_preview: asArray(root.lots_preview).reduce<NonNullable<QualityIndexResponse['lots_preview']>>((acc, item) => {
      if (!isRecord(item)) return acc;

      acc.push({
        purchase_lot_key: asString(item.purchase_lot_key),
        quality_index: asNumber(item.quality_index),
        units: asNumber(item.units),
        unmapped_units: asNumber(item.unmapped_units),
      });

      return acc;
    }, []),
    quality_report: normalizeQualityReport(root.quality_report),
  };
}

function computeState(
  globalMix: GlobalMixResponse,
  ranking: RankingResponse,
  detail: SupplierDetailResponse | null,
): { label: string; tone: Tone; narrative: string } {
  const unmappedShare = globalMix.mix?.unmapped_units_share ?? detail?.kpis?.unmapped_units_share ?? 0;
  const reasons = [
    ...(globalMix.quality_report?.reasons || []),
    ...(ranking.quality_report?.reasons || []),
    ...(detail?.quality_report?.reasons || []),
  ];
  const suppliers = ranking.ranking || [];
  const topSupplier = suppliers[0];
  const dominantGlobal = firstMappedQuality(globalMix.mix?.by_quality || []);
  const dominantLabel = dominantGlobal?.label || dominantGlobal?.quality_code || 'la calidad principal';
  const dominantShare = dominantGlobal?.units_share;

  if (!(globalMix.mix?.total_units || suppliers.length || detail?.kpis?.total_units)) {
    return {
      label: 'Sin base suficiente',
      tone: 'default',
      narrative: 'Todavía no hay información suficiente para construir una lectura ejecutiva de calidad vendible.',
    };
  }

  if (unmappedShare >= 0.2) {
    return {
      label: 'Cobertura parcial',
      tone: 'warning',
      narrative:
        'La señal de calidad ya es útil, pero una parte relevante del mix sigue sin clasificar. Conviene interpretar esta lectura como orientación ejecutiva y no como cierre final.',
    };
  }

  if (dominantLabel && dominantShare != null && topSupplier?.supplier) {
    if (reasons.length > 0) {
      return {
        label: 'Con cautela',
        tone: 'warning',
        narrative: `El mix vendible está dominado por ${dominantLabel} (${pct01(
          dominantShare,
        )} del volumen clasificado) y ${topSupplier.supplier} lidera la lectura actual, aunque persisten limitaciones de cobertura o mapping.`,
      };
    }

    return {
      label: 'Sólida',
      tone: 'success',
      narrative: `El mix vendible está dominado por ${dominantLabel} (${pct01(
        dominantShare,
      )} del volumen clasificado) y ${topSupplier.supplier} lidera la lectura actual por perfil de calidad.`,
    };
  }

  return {
    label: 'Desigual',
    tone: 'warning',
    narrative:
      'El negocio genera calidad vendible observable, pero no de forma homogénea entre proveedores. La media es útil, aunque no explica por sí sola el valor del mix.',
  };
}

function findTopQuality(row?: RankingRow): QualityMixRow | undefined {
  return (row?.by_quality || []).filter((item) => item.quality_code !== 'UNMAPPED').sort((a, b) => {
    const ar = a.rank ?? 999;
    const br = b.rank ?? 999;
    if (ar !== br) return ar - br;
    return (b.units_share || 0) - (a.units_share || 0);
  })[0];
}

function firstMappedQuality(rows: QualityMixRow[]): QualityMixRow | undefined {
  return rows.filter((item) => item.quality_code !== 'UNMAPPED').sort((a, b) => {
    const ar = a.rank ?? 999;
    const br = b.rank ?? 999;
    if (ar !== br) return ar - br;
    return (b.units_share || 0) - (a.units_share || 0);
  })[0];
}

const cardSx = {
  p: 2,
  borderRadius: 3,
  bgcolor: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  height: '100%',
};

export default function QualityVendibleView() {
  const [globalRaw, setGlobalRaw] = useState<unknown>(null);
  const [rankingRaw, setRankingRaw] = useState<unknown>(null);
  const [detailRaw, setDetailRaw] = useState<unknown>(null);
  const [indexRaw, setIndexRaw] = useState<unknown>(null);

  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sortBy, setSortBy] = useState<'premium_share' | 'low_share' | 'units' | 'revenue'>('premium_share');
  const [selectedSupplier, setSelectedSupplier] = useState<string>('');

  const globalMix = useMemo(() => normalizeGlobalMix(globalRaw), [globalRaw]);
  const ranking = useMemo(() => normalizeRanking(rankingRaw), [rankingRaw]);
  const detail = useMemo(() => (detailRaw == null ? null : normalizeSupplierDetail(detailRaw)), [detailRaw]);
  const qualityIndex = useMemo(() => (indexRaw == null ? null : normalizeQualityIndex(indexRaw)), [indexRaw]);

  const rankingRows = ranking.ranking || [];
  const globalByQuality = globalMix.mix?.by_quality || [];
  const supplierByQuality = detail?.by_quality || [];

  const state = useMemo(() => computeState(globalMix, ranking, detail), [globalMix, ranking, detail]);

  const leadSupplier = useMemo(
    () => rankingRows.find((row) => row.supplier === selectedSupplier) || rankingRows[0],
    [rankingRows, selectedSupplier],
  );

  const dominantGlobalQuality = useMemo(() => firstMappedQuality(globalByQuality), [globalByQuality]);
  const dominantSupplierQuality = useMemo(() => firstMappedQuality(supplierByQuality), [supplierByQuality]);
  const qualityMedian = qualityIndex?.distribution?.p50;
  const qualityP10 = qualityIndex?.distribution?.p10;
  const qualityP90 = qualityIndex?.distribution?.p90;
  const qualitySpread =
    qualityP10 != null && qualityP90 != null ? qualityP90 - qualityP10 : undefined;

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);

      try {
        const [mixRes, rankingRes] = await Promise.all([
          getIndustrialRiskQualityMix({
            product_code: 'MEJ',
            limit_lots: 5000,
          }),
          getIndustrialRiskQualityMixBySupplier({
            product_code: 'MEJ',
            top_n: 12,
            sort_by: sortBy,
          }),
        ]);

        if (cancelled) return;

        setGlobalRaw(mixRes);
        setRankingRaw(rankingRes);

        const normalizedRanking = normalizeRanking(rankingRes);
        const firstSupplier = normalizedRanking.ranking?.[0]?.supplier || '';
        setSelectedSupplier((prev) => prev || firstSupplier);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || e?.message || 'No se pudo cargar la lectura de calidad vendible.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [sortBy]);

  useEffect(() => {
    let cancelled = false;

    if (!selectedSupplier) {
      setDetailRaw(null);
      setIndexRaw(null);
      return () => {
        cancelled = true;
      };
    }

    const run = async () => {
      setDetailLoading(true);

      try {
        const [detailRes, indexRes] = await Promise.all([
          getIndustrialRiskQualityMixSupplier({
            product_code: 'MEJ',
            supplier: selectedSupplier,
            top_skus: 8,
          }),
          getIndustrialRiskQualityIndex({
            product_code: 'MEJ',
            supplier: selectedSupplier,
          }),
        ]);

        if (cancelled) return;

        setDetailRaw(detailRes);
        setIndexRaw(indexRes);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || e?.message || 'No se pudo cargar el detalle de calidad por proveedor.');
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [selectedSupplier]);

  return (
    <Stack spacing={2.5}>
      <Box
        sx={{
          p: 2.4,
          borderRadius: 4,
          bgcolor: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Stack spacing={1.1}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={1.2}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: -0.6 }}>
                Calidad vendible
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.82, mt: 0.5 }}>
                Lectura ejecutiva del mix producido, su valor observable y las diferencias reales entre proveedores.
              </Typography>
            </Box>

            <Chip label={state.label} color={state.tone} variant="filled" sx={{ fontWeight: 800 }} />
          </Stack>

          <Typography variant="body1" sx={{ opacity: 0.92 }}>
            {state.narrative}
          </Typography>
        </Stack>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress color="inherit" />
        </Box>
      ) : null}

      {!loading && !error ? (
        <>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} lg={3}>
              <Box sx={cardSx}>
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  Share premium
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {pct01(leadSupplier?.premium_share)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                  Peso de la calidad alta en el proveedor líder del ranking actual.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Box sx={cardSx}>
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  Calidad sin clasificar
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {pct01(globalMix.mix?.unmapped_units_share)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                  Parte del mix que todavía no está mapeada a una calidad vendible.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Box sx={cardSx}>
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  Proveedor líder en calidad
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {leadSupplier?.supplier || '—'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                  {findTopQuality(leadSupplier)?.label && leadSupplier?.premium_share != null
                    ? `Predomina ${findTopQuality(leadSupplier)?.label} y concentra ${pct01(
                        leadSupplier.premium_share,
                      )} de calidad alta.`
                    : 'Proveedor con mejor perfil según el orden seleccionado.'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Box sx={cardSx}>
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  Unidades vendibles analizadas
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {fmtNumber(globalMix.mix?.total_units)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                  Volumen total usado para construir esta lectura de calidad.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={1.5}>
            <Grid item xs={12} lg={7}>
              <Box
                sx={{
                  p: 2.2,
                  borderRadius: 4,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      Mix global por calidad
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.4 }}>
                      Qué calidad vendible está generando realmente el negocio y cuánto valor económico concentra cada tramo.
                    </Typography>
                  </Box>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}>
                          Calidad
                        </TableCell>
                        <TableCell
                          sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}
                          align="right"
                        >
                          Unidades
                        </TableCell>
                        <TableCell
                          sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}
                          align="right"
                        >
                          Share unidades
                        </TableCell>
                        <TableCell
                          sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}
                          align="right"
                        >
                          Revenue
                        </TableCell>
                        <TableCell
                          sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}
                          align="right"
                        >
                          Share revenue
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {globalByQuality.map((row) => (
                        <TableRow key={`${row.quality_code}-${row.rank}`}>
                          <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: '#fff', fontWeight: 700 }}>
                            {row.label || row.quality_code || '—'}
                          </TableCell>
                          <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                            {fmtNumber(row.units)}
                          </TableCell>
                          <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                            {pct01(row.units_share)}
                          </TableCell>
                          <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                            {eur(row.revenue_eur, 0)}
                          </TableCell>
                          <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                            {pct01(row.revenue_share)}
                          </TableCell>
                        </TableRow>
                      ))}

                      {!globalByQuality.length ? (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.72)' }}>
                            No hay mix suficiente para construir la lectura global de calidad.
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>

                  <Typography variant="body2" sx={{ opacity: 0.75 }}>
                    {dominantGlobalQuality?.label
                      ? `${dominantGlobalQuality.label} domina el mix global, pero su peso en valor no coincide exactamente con su peso en unidades.`
                      : 'Todavía no hay una calidad dominante suficientemente clara en el mix global.'}
                  </Typography>
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Box
                sx={{
                  p: 2.2,
                  borderRadius: 4,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      Lectura ejecutiva del mix
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.4 }}>
                      Qué señales merece la pena llevar a comité antes de bajar al detalle por proveedor.
                    </Typography>
                  </Box>

                  <Box sx={cardSx}>
                    <Typography variant="body2" sx={{ opacity: 0.72 }}>
                      Revenue observable del mix
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                      {eur(globalMix.mix?.total_revenue_eur, 0)}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                      Valor económico estimado a partir de los outputs observados y su mix por calidad.
                    </Typography>
                  </Box>

                  <Box sx={cardSx}>
                    <Typography variant="body2" sx={{ opacity: 0.72 }}>
                      Calidad dominante
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                      {dominantGlobalQuality?.label || '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                      {dominantGlobalQuality?.units_share != null
                        ? `Concentra ${pct01(dominantGlobalQuality.units_share)} del volumen clasificado.`
                        : 'No hay una calidad dominante suficientemente estable.'}
                    </Typography>
                  </Box>

                  <Box sx={cardSx}>
                    <Typography variant="body2" sx={{ opacity: 0.72 }}>
                      SKUs principales del periodo
                    </Typography>
                    <Stack spacing={0.8} sx={{ mt: 1 }}>
                      {(globalMix.mix?.top_skus_by_units || []).slice(0, 5).map((sku) => (
                        <Stack key={sku.sku_code} direction="row" justifyContent="space-between" spacing={1}>
                          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 700 }}>
                            {sku.sku_code || '—'}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.82 }}>
                            {fmtNumber(sku.units)}
                          </Typography>
                        </Stack>
                      ))}

                      {!globalMix.mix?.top_skus_by_units?.length ? (
                        <Typography variant="body2" sx={{ opacity: 0.72 }}>
                          No hay SKUs suficientes para mostrar concentración del mix.
                        </Typography>
                      ) : null}
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2.2,
                  borderRadius: 4,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <Stack spacing={1.5}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    spacing={1.4}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 900 }}>
                        Ranking de proveedores por perfil de calidad
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.4 }}>
                        No todos los proveedores convierten el kg comprado en el mismo mix vendible ni en la misma proporción de calidad alta.
                      </Typography>
                    </Box>

                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel sx={{ color: 'rgba(255,255,255,0.72)' }}>Orden</InputLabel>
                      <Select
                        value={sortBy}
                        label="Orden"
                        onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                        sx={{
                          color: '#fff',
                          '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                          '& .MuiSvgIcon-root': { color: '#fff' },
                        }}
                      >
                        <MenuItem value="premium_share">Más premium</MenuItem>
                        <MenuItem value="low_share">Más low</MenuItem>
                        <MenuItem value="units">Más unidades</MenuItem>
                        <MenuItem value="revenue">Más revenue</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}>
                          Proveedor
                        </TableCell>
                        <TableCell
                          sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}
                          align="right"
                        >
                          Unidades
                        </TableCell>
                        <TableCell
                          sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}
                          align="right"
                        >
                          Revenue
                        </TableCell>
                        <TableCell
                          sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}
                          align="right"
                        >
                          Share premium
                        </TableCell>
                        <TableCell
                          sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}
                          align="right"
                        >
                          Share low
                        </TableCell>
                        <TableCell
                          sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}
                          align="right"
                        >
                          Sin clasificar
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rankingRows.map((row) => (
                        <TableRow
                          key={row.supplier}
                          hover
                          selected={row.supplier === selectedSupplier}
                          onClick={() => setSelectedSupplier(row.supplier || '')}
                          sx={{
                            cursor: 'pointer',
                            '& .MuiTableCell-root': {
                              borderBottomColor: 'rgba(255,255,255,0.06)',
                            },
                          }}
                        >
                          <TableCell sx={{ color: '#fff', fontWeight: 700 }}>
                            {row.supplier || '—'}
                          </TableCell>
                          <TableCell sx={{ color: 'rgba(255,255,255,0.88)' }} align="right">
                            {fmtNumber(row.total_units)}
                          </TableCell>
                          <TableCell sx={{ color: 'rgba(255,255,255,0.88)' }} align="right">
                            {eur(row.total_revenue_eur, 0)}
                          </TableCell>
                          <TableCell sx={{ color: 'rgba(255,255,255,0.88)' }} align="right">
                            {pct01(row.premium_share)}
                          </TableCell>
                          <TableCell sx={{ color: 'rgba(255,255,255,0.88)' }} align="right">
                            {pct01(row.low_share)}
                          </TableCell>
                          <TableCell sx={{ color: 'rgba(255,255,255,0.88)' }} align="right">
                            {pct01(row.unmapped_units_share)}
                          </TableCell>
                        </TableRow>
                      ))}

                      {!rankingRows.length ? (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.72)' }}>
                            No hay proveedores suficientes para construir el ranking.
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>

                  <Typography variant="caption" sx={{ opacity: 0.65 }}>
                    Pulsa un proveedor para ver su detalle. El orden del ranking cambia la lectura prioritaria, no el dato subyacente.
                  </Typography>
                </Stack>
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={1.5}>
            <Grid item xs={12} lg={4}>
              <Box
                sx={{
                  p: 2.2,
                  borderRadius: 4,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Stack spacing={1.5}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    spacing={1.2}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 900 }}>
                        Detalle por proveedor
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.25 }}>
                        Perfil de calidad y valor observable del proveedor seleccionado.
                      </Typography>
                    </Box>

                    <FormControl size="small" sx={{ minWidth: 220, mt: 0.2 }}>
                      <InputLabel sx={{ color: 'rgba(255,255,255,0.72)' }}>Proveedor</InputLabel>
                      <Select
                        value={selectedSupplier}
                        label="Proveedor"
                        onChange={(e) => setSelectedSupplier(e.target.value)}
                        sx={{
                          color: '#fff',
                          '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                          '& .MuiSvgIcon-root': { color: '#fff' },
                        }}
                      >
                        {rankingRows.map((row) => (
                          <MenuItem key={row.supplier} value={row.supplier}>
                            {row.supplier}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>

                  {detailLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                      <CircularProgress color="inherit" size={28} />
                    </Box>
                  ) : (
                    <>
                      <Grid container spacing={1.2}>
                        <Grid item xs={12} sm={6} lg={12}>
                          <Box sx={{ ...cardSx, p: 1.6 }}>
                            <Typography variant="body2" sx={{ opacity: 0.72 }}>
                              Índice de calidad
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.35 }}>
                              {indexFmt(qualityIndex?.distribution?.p50)}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.35 }}>
                              Mediana observada del proveedor seleccionado.
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} lg={12}>
                          <Box sx={{ ...cardSx, p: 1.6 }}>
                            <Typography variant="body2" sx={{ opacity: 0.72 }}>
                              Unidades del proveedor
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                              {fmtNumber(detail?.kpis?.total_units)}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                              Volumen usado para construir su mix y su índice de calidad.
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} lg={12}>
                          <Box sx={{ ...cardSx, p: 1.6 }}>
                            <Typography variant="body2" sx={{ opacity: 0.72 }}>
                              Revenue del proveedor
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                              {eur(detail?.kpis?.total_revenue_eur, 0)}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                              Valor observable del mix vendido asociado a este proveedor.
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} lg={12}>
                          <Box sx={{ ...cardSx, p: 1.6 }}>
                            <Typography variant="body2" sx={{ opacity: 0.72 }}>
                              Calidad dominante del proveedor
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                              {dominantSupplierQuality?.label || '—'}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                              {dominantSupplierQuality?.units_share != null
                                ? `Concentra ${pct01(dominantSupplierQuality.units_share)} del volumen clasificado del proveedor.`
                                : 'No hay una dominante clara con la información actual.'}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} lg={8}>
              <Stack spacing={1.5} sx={{ height: '100%' }}>
                <Box
                  sx={{
                    p: 2.2,
                    borderRadius: 4,
                    bgcolor: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 900 }}>
                        Mix del proveedor seleccionado
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.4 }}>
                        Cómo se reparte el valor vendible del proveedor entre calidades.
                      </Typography>
                    </Box>

                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}>
                            Calidad
                          </TableCell>
                          <TableCell
                            sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}
                            align="right"
                          >
                            Unidades
                          </TableCell>
                          <TableCell
                            sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}
                            align="right"
                          >
                            Share unidades
                          </TableCell>
                          <TableCell
                            sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}
                            align="right"
                          >
                            Revenue
                          </TableCell>
                          <TableCell
                            sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}
                            align="right"
                          >
                            Share revenue
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {supplierByQuality.map((row) => (
                          <TableRow key={`${row.quality_code}-${row.rank}`}>
                            <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: '#fff', fontWeight: 700 }}>
                              {row.label || row.quality_code || '—'}
                            </TableCell>
                            <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                              {fmtNumber(row.units)}
                            </TableCell>
                            <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                              {pct01(row.units_share)}
                            </TableCell>
                            <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                              {eur(row.revenue_eur, 0)}
                            </TableCell>
                            <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                              {pct01(row.revenue_share)}
                            </TableCell>
                          </TableRow>
                        ))}

                        {!supplierByQuality.length ? (
                          <TableRow>
                            <TableCell colSpan={5} sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.72)' }}>
                              No hay mix suficiente para este proveedor.
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </TableBody>
                    </Table>
                  </Stack>
                </Box>

                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        p: 2.2,
                        borderRadius: 4,
                        bgcolor: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        height: '100%',
                      }}
                    >
                      <Stack spacing={1.2}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 900 }}>
                            Estabilidad del índice
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.4 }}>
                            Qué tan estable es la calidad observada entre lotes del proveedor seleccionado.
                          </Typography>
                        </Box>

                        <Box sx={cardSx}>
                          <Typography variant="body2" sx={{ opacity: 0.72 }}>
                            Mediana
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                            {indexFmt(qualityMedian)}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                            Perfil {qualityBandLabel(qualityMedian).toLowerCase()} de calidad observada.
                          </Typography>
                        </Box>

                        <Box sx={cardSx}>
                          <Typography variant="body2" sx={{ opacity: 0.72 }}>
                            Rango habitual
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                            {qualityP10 != null && qualityP90 != null
                              ? `${indexFmt(qualityP10)} – ${indexFmt(qualityP90)}`
                              : '—'}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                            Banda central observada entre lotes comparables.
                          </Typography>
                        </Box>

                        <Box sx={cardSx}>
                          <Typography variant="body2" sx={{ opacity: 0.72 }}>
                            Dispersión
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                            {indexFmt(qualitySpread)}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                            Cuanto menor es la dispersión, más predecible es el perfil de calidad.
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        p: 2.2,
                        borderRadius: 4,
                        bgcolor: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        height: '100%',
                      }}
                    >
                      <Stack spacing={1.2}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 900 }}>
                            Top SKUs por unidades
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.4 }}>
                            Qué referencias están tirando del volumen del proveedor.
                          </Typography>
                        </Box>

                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}>
                                SKU
                              </TableCell>
                              <TableCell
                                sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}
                                align="right"
                              >
                                Unidades
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(detail?.top_skus_by_units || []).map((row) => (
                              <TableRow key={row.sku_code}>
                                <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: '#fff', fontWeight: 700 }}>
                                  {row.sku_code || '—'}
                                </TableCell>
                                <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                                  {fmtNumber(row.units)}
                                </TableCell>
                              </TableRow>
                            ))}

                            {!detail?.top_skus_by_units?.length ? (
                              <TableRow>
                                <TableCell colSpan={2} sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.72)' }}>
                                  No hay SKUs suficientes para este proveedor.
                                </TableCell>
                              </TableRow>
                            ) : null}
                          </TableBody>
                        </Table>
                      </Stack>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        p: 2.2,
                        borderRadius: 4,
                        bgcolor: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        height: '100%',
                      }}
                    >
                      <Stack spacing={1.2}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 900 }}>
                            Top SKUs por revenue
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.4 }}>
                            Qué referencias están concentrando más valor económico observable.
                          </Typography>
                        </Box>

                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}>
                                SKU
                              </TableCell>
                              <TableCell
                                sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}
                                align="right"
                              >
                                Revenue
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(detail?.top_skus_by_revenue || []).map((row) => (
                              <TableRow key={row.sku_code}>
                                <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: '#fff', fontWeight: 700 }}>
                                  {row.sku_code || '—'}
                                </TableCell>
                                <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                                  {eur(row.revenue_eur, 0)}
                                </TableCell>
                              </TableRow>
                            ))}

                            {!detail?.top_skus_by_revenue?.length ? (
                              <TableRow>
                                <TableCell colSpan={2} sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.72)' }}>
                                  No hay revenue suficiente para este proveedor.
                                </TableCell>
                              </TableRow>
                            ) : null}
                          </TableBody>
                        </Table>
                      </Stack>
                    </Box>
                  </Grid>
                </Grid>
              </Stack>
            </Grid>
          </Grid>

          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2.2,
                  borderRadius: 4,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <Stack spacing={1.4}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      Cobertura y lectura metodológica
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.4 }}>
                      Esta pantalla es una lectura económica estimada del mix y su calidad vendible; no es un cierre contable oficial.
                    </Typography>
                  </Box>

                  <Grid container spacing={1.2}>
                    <Grid item xs={12} md={3}>
                      <Box sx={cardSx}>
                        <Typography variant="body2" sx={{ opacity: 0.72 }}>
                          Lotes del proveedor
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                          {fmtNumber(detail?.quality_report?.data_coverage?.supplier_lots_considered)}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                          Lotes considerados en el detalle del proveedor seleccionado.
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Box sx={cardSx}>
                        <Typography variant="body2" sx={{ opacity: 0.72 }}>
                          Outputs leídos
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                          {fmtNumber(ranking.quality_report?.data_coverage?.n_outputs_rows ?? globalMix.quality_report?.data_coverage?.n_outputs_rows)}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                          Base observable usada para construir mix y ranking.
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Box sx={cardSx}>
                        <Typography variant="body2" sx={{ opacity: 0.72 }}>
                          SKUs mapeados
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                          {fmtNumber(ranking.quality_report?.data_coverage?.n_mapped_skus ?? globalMix.quality_report?.data_coverage?.n_mapped_skus)}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                          SKUs con calidad definida dentro del catálogo utilizado.
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Box sx={cardSx}>
                        <Typography variant="body2" sx={{ opacity: 0.72 }}>
                          Calidad definida
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                          {fmtNumber(ranking.quality_report?.data_coverage?.n_qualities_defined ?? globalMix.quality_report?.data_coverage?.n_qualities_defined)}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                          Número de tramos de calidad definidos para este producto.
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {(globalMix.quality_report?.reasons?.length ||
                    ranking.quality_report?.reasons?.length ||
                    detail?.quality_report?.reasons?.length ||
                    qualityIndex?.quality_report?.reasons?.length) ? (
                    <Alert severity="warning" sx={{ bgcolor: 'rgba(245, 158, 11, 0.12)', color: '#fde68a' }}>
                      {[
                        ...(globalMix.quality_report?.reasons || []),
                        ...(ranking.quality_report?.reasons || []),
                        ...(detail?.quality_report?.reasons || []),
                        ...(qualityIndex?.quality_report?.reasons || []),
                      ].join(' · ')}
                    </Alert>
                  ) : null}

                  <Typography variant="caption" sx={{ opacity: 0.65 }}>
                    Entorno orientado a lectura ejecutiva. Si el dataset sigue siendo parcial o hay SKUs sin mapping, esta vista debe interpretarse como guía estructural y no como decisión final cerrada.
                  </Typography>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </>
      ) : null}
    </Stack>
  );
}